import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { 
  GraduationCap, BookOpen, Award, Save, Plus, Trash2, 
  CheckCircle, AlertCircle, ChevronDown, ChevronUp
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const qualificationOptions = [
  { value: '10th', label: '10th (High School)', levels: ['10th'] },
  { value: '12th', label: '12th (Intermediate)', levels: ['10th', '12th'] },
  { value: 'graduation', label: 'Graduation (UG)', levels: ['10th', '12th', 'graduation'] },
  { value: 'post_graduation', label: 'Post Graduation (PG)', levels: ['10th', '12th', 'graduation', 'post_graduation'] }
];

const levelLabels = {
  '10th': { title: '10th (High School)', boardLabel: 'Board Name' },
  '12th': { title: '12th (Intermediate)', boardLabel: 'Board Name' },
  'graduation': { title: 'Graduation', boardLabel: 'University Name' },
  'post_graduation': { title: 'Post Graduation', boardLabel: 'University Name' }
};

const EducationForm = ({ onUpdate, initialData }) => {
  const [highestQualification, setHighestQualification] = useState('');
  const [educationDetails, setEducationDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedLevel, setExpandedLevel] = useState(null);

  useEffect(() => {
    if (initialData) {
      setHighestQualification(initialData.highest_qualification || '');
      setEducationDetails(initialData.education_details || []);
    }
  }, [initialData]);

  // When highest qualification changes, create empty forms for all required levels
  const handleQualificationChange = (qualification) => {
    setHighestQualification(qualification);
    const selectedOption = qualificationOptions.find(q => q.value === qualification);
    
    if (selectedOption) {
      const newDetails = selectedOption.levels.map(level => {
        // Keep existing data if available
        const existing = educationDetails.find(e => e.level === level);
        return existing || {
          level,
          board_or_university: '',
          passing_year: '',
          percentage: '',
          total_marks: '',
          obtained_marks: ''
        };
      });
      setEducationDetails(newDetails);
      setExpandedLevel(selectedOption.levels[selectedOption.levels.length - 1]);
    }
  };

  const handleDetailChange = (level, field, value) => {
    setEducationDetails(prev => 
      prev.map(edu => 
        edu.level === level ? { ...edu, [field]: value } : edu
      )
    );
  };

  const handleSave = async () => {
    if (!highestQualification) {
      toast.error('Please select your highest qualification');
      return;
    }

    // Validate all required fields
    for (const edu of educationDetails) {
      if (!edu.board_or_university || !edu.passing_year || !edu.percentage) {
        toast.error(`Please fill all required fields for ${levelLabels[edu.level].title}`);
        setExpandedLevel(edu.level);
        return;
      }
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/student/education`, {
        highest_qualification: highestQualification,
        education_details: educationDetails.map(edu => ({
          ...edu,
          passing_year: parseInt(edu.passing_year),
          percentage: parseFloat(edu.percentage),
          total_marks: edu.total_marks ? parseInt(edu.total_marks) : null,
          obtained_marks: edu.obtained_marks ? parseInt(edu.obtained_marks) : null
        }))
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Education details saved successfully!');
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save education details');
    } finally {
      setLoading(false);
    }
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 50 }, (_, i) => currentYear - i);

  return (
    <div className="space-y-6">
      {/* Step 1: Select Highest Qualification */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100">
        <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
          <GraduationCap className="w-5 h-5 text-blue-600" />
          Select Your Highest Qualification
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {qualificationOptions.map(option => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleQualificationChange(option.value)}
              className={`p-4 rounded-xl border-2 transition-all text-left ${
                highestQualification === option.value
                  ? 'border-blue-500 bg-blue-50 shadow-md'
                  : 'border-slate-200 bg-white hover:border-blue-300'
              }`}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-2 ${
                highestQualification === option.value ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-600'
              }`}>
                {highestQualification === option.value ? (
                  <CheckCircle className="w-5 h-5" />
                ) : (
                  <BookOpen className="w-4 h-4" />
                )}
              </div>
              <p className={`font-medium text-sm ${
                highestQualification === option.value ? 'text-blue-700' : 'text-slate-700'
              }`}>
                {option.label}
              </p>
            </button>
          ))}
        </div>
      </div>

      {/* Step 2: Fill Education Details */}
      {highestQualification && educationDetails.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <Award className="w-5 h-5 text-emerald-600" />
            Fill Your Education Details
          </h3>
          <p className="text-sm text-slate-500">
            Please fill details for all qualification levels up to your highest qualification.
          </p>

          {educationDetails.map((edu, index) => {
            const levelInfo = levelLabels[edu.level];
            const isExpanded = expandedLevel === edu.level;
            const isFilled = edu.board_or_university && edu.passing_year && edu.percentage;

            return (
              <div 
                key={edu.level}
                className={`border-2 rounded-xl overflow-hidden transition-all ${
                  isFilled ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-200 bg-white'
                }`}
              >
                {/* Header */}
                <button
                  type="button"
                  onClick={() => setExpandedLevel(isExpanded ? null : edu.level)}
                  className="w-full p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      isFilled ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-600'
                    }`}>
                      {isFilled ? <CheckCircle className="w-5 h-5" /> : index + 1}
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-slate-800">{levelInfo.title}</p>
                      {isFilled && (
                        <p className="text-xs text-slate-500">
                          {edu.board_or_university} | {edu.passing_year} | {edu.percentage}%
                        </p>
                      )}
                    </div>
                  </div>
                  {isExpanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                </button>

                {/* Form Fields */}
                {isExpanded && (
                  <div className="p-4 pt-0 border-t border-slate-100">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      <div>
                        <Label className="text-slate-700 mb-1.5 block">{levelInfo.boardLabel} *</Label>
                        <Input
                          value={edu.board_or_university}
                          onChange={(e) => handleDetailChange(edu.level, 'board_or_university', e.target.value)}
                          placeholder={edu.level.includes('graduation') ? 'e.g., Delhi University' : 'e.g., CBSE, RBSE'}
                          className="border-slate-200"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-700 mb-1.5 block">Passing Year *</Label>
                        <select
                          value={edu.passing_year}
                          onChange={(e) => handleDetailChange(edu.level, 'passing_year', e.target.value)}
                          className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-slate-800"
                        >
                          <option value="">Select Year</option>
                          {years.map(year => (
                            <option key={year} value={year}>{year}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label className="text-slate-700 mb-1.5 block">Percentage (%) *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          value={edu.percentage}
                          onChange={(e) => handleDetailChange(edu.level, 'percentage', e.target.value)}
                          placeholder="e.g., 75.5"
                          className="border-slate-200"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-700 mb-1.5 block">Total Marks (Optional)</Label>
                        <Input
                          type="number"
                          value={edu.total_marks}
                          onChange={(e) => handleDetailChange(edu.level, 'total_marks', e.target.value)}
                          placeholder="e.g., 500"
                          className="border-slate-200"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Save Button */}
      {highestQualification && (
        <div className="flex justify-end pt-4">
          <Button 
            onClick={handleSave} 
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 px-8"
          >
            <Save className="w-4 h-4 mr-2" />
            {loading ? 'Saving...' : 'Save Education Details'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default EducationForm;
